import { PropsWithChildren, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as Accordion from '@givebutter/primitives/accordion';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import * as Collapsible from '@givebutter/primitives/collapsible';
import { Label } from '@givebutter/primitives/label';
import * as Popover from '@givebutter/primitives/popover';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import {
    faArrowRight,
    faChevronDown,
    faCircle,
    faCircleCheck,
    faEnvelope,
    faExclamationCircle,
    faMagnifyingGlass,
    faPlus,
    faQuestionCircle,
    faRepeat,
    faSpinner,
    faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faBadgeCheck,
    faDollar,
    faInputNumeric,
    faPhoneCircle,
    faPhoneSlash,
    faShield,
    faSms,
    faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    createEngageMessage,
    deleteEngageMessageNumber as deleteEngageMessageNumberApi,
    getAvailableNumbers as getAvailableNumbersApi,
    getEngageConfiguration,
    postPhoneNumber as postPhoneNumberApi,
    provisionEngageRegistration as provisionEngageRegistrationApi,
    updateEngageMessageNumber as updateEngageMessageNumberApi,
} from 'api/engage';
import { CopyButton } from 'components/copy-button';
import * as Form from 'components/form';
import { CONTACT_US_LINK } from 'constants/links';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import AttLogo from 'images/att_logo.png';
import EngageSmsHeader from 'images/engage-sms-header.webp';
import TmobileLogo from 'images/tmobile_logo.png';
import VerizonLogo from 'images/verizon_logo.png';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twMerge } from 'tailwind-merge';
import { EngageConfiguration } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

enum SMSConfigurationSteps {
    VerifyAccount = 'verifyAccount',
    SelectPhoneNumber = 'selectPhoneNumber',
    SubmitApplication = 'submitApplication',
    SendFirstMessage = 'sendFirstMessage',
}

interface UpdateEngageMessageNumberFormValues {
    forwardingEmail: string;
    responseMessage: string;
    forwardingCriteria: string;
}

const NumberSettings = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId, postalAddress, legalEntity, phone, email, website } = useCurrentAccount();
    const methods = Form.useGivebutterForm({
        defaultValues: { search: '' },
    });
    const { handleSubmit, getValues, control } = methods;
    const [isPendingAPIRequest, setIsPendingAPIRequest] = useState<boolean>(false);
    const [availableNumbers, setAvailableNumbers] = useState<Array<string>>([]);
    const [selectedNumber, setSelectedNumber] = useState<string>('');
    const [showApplicationSentModal, setShowApplicationSentModal] = useState<boolean>(false);
    const [openGetStartedPopover, setOpenGetStartedPopover] = useState(false);
    const handleMouseEnterGetStartedPopover = () => {
        setOpenGetStartedPopover(true);
    };
    const handleMouseLeaveGetStartedPopover = () => {
        setOpenGetStartedPopover(false);
    };
    const clearFilterByKey = useFiltersCache(state => state.clearFilterByKey);
    const history = useHistory();
    const {
        hasDismissedSMSFeatureCallout,
        setHasDismissedSMSFeatureCallout,
        hasDismissedSMSRegistrationSuccessNotice,
        setHasDismissedSMSRegistrationSuccessNotice,
        hasDismissedSMSRegistrationErrorNotice,
        setHasDismissedSMSRegistrationErrorNotice,
    } = useLocalStorage(
        ({
            hasDismissedSMSFeatureCallout,
            setHasDismissedSMSFeatureCallout,
            hasDismissedSMSRegistrationSuccessNotice,
            setHasDismissedSMSRegistrationSuccessNotice,
            hasDismissedSMSRegistrationErrorNotice,
            setHasDismissedSMSRegistrationErrorNotice,
        }) => {
            return {
                hasDismissedSMSFeatureCallout,
                setHasDismissedSMSFeatureCallout,
                hasDismissedSMSRegistrationSuccessNotice,
                setHasDismissedSMSRegistrationSuccessNotice,
                hasDismissedSMSRegistrationErrorNotice,
                setHasDismissedSMSRegistrationErrorNotice,
            };
        },
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageConfiguration(accountId),
        queryFn: () => getEngageConfiguration(accountId),
        select: data => ({
            applicationPending:
                data?.channels.sms.regulatory_status !== 'failed' &&
                data?.channels.sms.regulatory_status !== null,
            applicationApproved: data?.channels.sms.regulatory_status === 'approved',
            ...data,
        }),
    });

    const messageNumber = data?.channels.sms.number;

    const methodsAutoResponse = Form.useGivebutterForm<UpdateEngageMessageNumberFormValues>({
        defaultValues: {
            forwardingEmail: messageNumber?.forwarding_email ?? '',
            responseMessage: messageNumber?.response_message ?? '',
            forwardingCriteria: messageNumber?.forwarding_criteria ?? 'known',
        },
    });

    const handleRequestError = (error: GivebutterAxiosError) => {
        toast.error(
            error.response?.data.message !== undefined && error.response.data.message !== ''
                ? error.response.data.message
                : 'Something went wrong. Please try again. If this error persists, contact support.',
        );
    };

    const {
        handleSubmit: handleSubmitAutoResponse,
        control: controlAutoResponse,
        path: pathAutoResponse,
        watch: watchAutoResponse,
    } = methodsAutoResponse;
    const [forwardingCriteria] = watchAutoResponse(['forwardingCriteria']);

    const { mutate: getAvailableNumbers } = useMutation({
        mutationFn: () => {
            setIsPendingAPIRequest(true);
            const { search } = getValues();
            return getAvailableNumbersApi(accountId, { search });
        },
        onSuccess: response => {
            setAvailableNumbers(response.slice(0, 6));
        },
        onError: (error: GivebutterAxiosError) => {
            handleRequestError(error);
        },
        onSettled: () => setIsPendingAPIRequest(false),
    });

    const { mutate: postPhoneNumber } = useMutation({
        mutationFn: () => {
            setIsPendingAPIRequest(true);
            return postPhoneNumberApi(accountId, {
                phoneNumber: selectedNumber,
            });
        },
        onSuccess: () => {
            toast.success(`Successfully claimed phone number ${selectedNumber}`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageConfiguration(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            handleRequestError(error);
        },
        onSettled: () => setIsPendingAPIRequest(false),
    });

    const { mutate: provisionEngageRegistration } = useMutation({
        mutationFn: () => {
            if (hasDismissedSMSRegistrationErrorNotice) {
                setHasDismissedSMSRegistrationErrorNotice(false);
            }
            return provisionEngageRegistrationApi(accountId);
        },
        onSuccess: () => {
            setShowApplicationSentModal(true);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageConfiguration(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            handleRequestError(error);
        },
        onSettled: () => setIsPendingAPIRequest(false),
    });

    const { mutate: onClickCreateMessage } = useMutation({
        mutationFn: () => {
            return createEngageMessage(accountId, { channel: 'sms' });
        },
        onSuccess: response => {
            clearFilterByKey('engage');
            history.push(`/accounts/${accountId}/engage/${response.channel}/${response.id}/edit`);
        },
        onError: (error: GivebutterAxiosError) => {
            handleRequestError(error);
        },
    });

    const { mutate: updateEngageMessageNumber } = useMutation({
        mutationFn: (values: UpdateEngageMessageNumberFormValues) =>
            updateEngageMessageNumberApi(accountId, messageNumber?.id ?? 0, {
                forwarding_email: values.forwardingEmail,
                forwarding_criteria: values.forwardingCriteria,
                response_message: values.responseMessage,
            }),
        onSuccess: () => {
            toast.success(`Successfully updated auto-response`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageConfiguration(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            handleRequestError(error);
        },
    });

    const { mutate: deleteEngageMessageNumber } = useMutation({
        mutationFn: () => deleteEngageMessageNumberApi(accountId, messageNumber?.id ?? 0),
        onSuccess: () => {
            toast.success(`Successfully updated auto-response`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageConfiguration(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            handleRequestError(error);
        },
    });

    const { currentStep, stepsCompleted } = useCurrentStep({ engageConfiguration: data });

    const { sponsoringLegalEntity } = useCurrentAccount();

    const submitBtnText = data?.applicationPending ? 'Registration Pending' : 'Submit application';

    return (
        <div className={'flex flex-col gap-4'}>
            {data?.channels.sms.regulatory_status === 'unknown' && (
                <div className="bg-red-25 relative rounded-lg border border-red-300 px-11 py-3 text-red-700">
                    <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="absolute left-3.5 top-3.5"
                    />
                    <div className="text-sm font-medium">10DLC Application Error</div>
                    <div className="text-xs">
                        An unknown error occurred submitting your application, please{' '}
                        <a href={CONTACT_US_LINK} target="_blank" rel="noreferrer">
                            contact us
                        </a>
                        .
                    </div>
                </div>
            )}

            {data?.channels.sms.regulatory_status === 'failed' &&
                !data?.onboarding.has_number &&
                !hasDismissedSMSRegistrationErrorNotice && (
                    <div className="bg-red-25 relative rounded-lg border border-red-300 px-11 py-3 text-red-700">
                        <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="absolute left-3.5 top-3.5"
                        />
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="absolute right-3.5 top-3.5 cursor-pointer"
                            onClick={() => {
                                setHasDismissedSMSRegistrationErrorNotice(true);
                            }}
                        />
                        <div className="text-sm font-medium">Application Rejected</div>
                        <div className="text-xs">
                            Your application has been rejected. This is usually due to inaccurate or
                            false information provided. Please edit and confirm your organization's
                            details before attempting to register again.
                        </div>
                    </div>
                )}

            {data?.channels.sms.regulatory_status === 'failed' && data?.onboarding.has_number && (
                <div className="bg-red-25 relative rounded-lg border border-red-300 px-11 py-3 text-red-700">
                    <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="absolute left-3.5 top-3.5"
                    />
                    <div className="text-sm font-medium">Application Rejected</div>
                    <div className="text-xs">
                        Please{' '}
                        <a href={CONTACT_US_LINK} target="_blank" rel="noreferrer">
                            contact us
                        </a>{' '}
                        for more details and to assist with your application.
                    </div>
                </div>
            )}

            {!data?.onboarding.has_number &&
                data?.applicationApproved &&
                !hasDismissedSMSRegistrationSuccessNotice && (
                    <div className="bg-green-25 relative rounded-lg border border-green-300 px-11 py-3 text-green-700">
                        <FontAwesomeIcon
                            icon={faCircleCheck}
                            className="absolute left-3.5 top-3.5"
                        />
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="absolute right-3.5 top-3.5 cursor-pointer"
                            onClick={() => {
                                setHasDismissedSMSRegistrationSuccessNotice(true);
                            }}
                        />
                        <div className="text-sm font-medium">Application Accepted!</div>
                        <div className="text-xs">
                            Your application was accepted. You're now able to set up a 10DLC phone
                            number.
                        </div>
                    </div>
                )}

            {data?.applicationApproved && !!Object.keys(messageNumber ?? {}).length && (
                <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-8 pb-6 shadow-sm">
                    <div className="flex justify-between">
                        <div className="relative">
                            <FontAwesomeIcon
                                icon={faPhoneCircle}
                                size="2xl"
                                className="text-gray-400"
                            />
                            <FontAwesomeIcon
                                icon={faBadgeCheck}
                                className={`absolute -bottom-1 -right-2 text-green-500`}
                            />
                        </div>
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={() => {
                                onClickCreateMessage();
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            New Message
                        </Button>
                    </div>
                    <div>
                        <div className="flex items-center gap-1">
                            Registered 10DLC
                            <Popover.Root>
                                <Popover.Trigger>
                                    <FontAwesomeIcon icon={faQuestionCircle} size={'xs'} />
                                </Popover.Trigger>
                                <Popover.Portal>
                                    <Popover.Content className="max-w-sm">
                                        <div className="relative p-6">
                                            <div className="text-xl font-medium">About 10DLC</div>
                                            <div className="flex flex-col gap-4">
                                                A 10DLC is your registered phone number that ensure
                                                that your organization's text messages will be
                                                delivered safely, legally, and in compliance with
                                                all major cellular carriers. This helps to ensure
                                                that your messages are reliably delivered to your
                                                intended recipients.
                                                <div className="grid grid-cols-3 items-center gap-10">
                                                    <img src={AttLogo} alt="AT&T Logo" />
                                                    <img src={TmobileLogo} alt="T-Mobile Logo" />
                                                    <img src={VerizonLogo} alt="Verizon Logo" />
                                                </div>
                                            </div>
                                            <Popover.Close className="absolute right-3 top-3" />
                                            <Popover.Arrow />
                                        </div>
                                    </Popover.Content>
                                </Popover.Portal>
                            </Popover.Root>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-2xl font-medium">
                                {getFormattedPhoneNumber(messageNumber?.value ?? '')}
                            </span>
                            <CopyButton
                                text={getFormattedPhoneNumber(messageNumber?.value as string)}
                            />
                        </div>
                    </div>
                    <Collapsible.Root className="flex flex-col gap-4">
                        <Collapsible.Trigger className="group flex items-center gap-2 text-sm font-medium text-blue-500">
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="group-radix-state-open:rotate-180"
                            />
                            Incoming Text Settings
                        </Collapsible.Trigger>
                        <Collapsible.Content>
                            <Form.GivebutterFormProvider {...methodsAutoResponse}>
                                <form
                                    className="flex flex-col gap-2 text-sm"
                                    onSubmit={handleSubmitAutoResponse(
                                        updateEngageMessageNumber as SubmitHandler<UpdateEngageMessageNumberFormValues>,
                                    )}
                                >
                                    <Form.Container>
                                        <Form.Label name={pathAutoResponse('responseMessage')}>
                                            Default Auto Response
                                        </Form.Label>
                                        <Form.UseFormTextArea
                                            name={pathAutoResponse('responseMessage')}
                                            control={controlAutoResponse}
                                            rows={5}
                                            placeholder={messageNumber?.default_response_message}
                                        />
                                    </Form.Container>

                                    <Form.Container>
                                        <Form.Label
                                            name={pathAutoResponse('forwardingCriteria')}
                                            className="flex items-center gap-2"
                                        >
                                            Forward incoming texts to my email
                                        </Form.Label>
                                        <Form.SingleSelect
                                            control={controlAutoResponse}
                                            name="forwardingCriteria"
                                        >
                                            <SingleSelect.Trigger aria-label="Incoming text forwarding" />
                                            <SingleSelect.Content>
                                                <SingleSelect.Item
                                                    value="none"
                                                    className="flex-col items-start gap-1"
                                                >
                                                    <p className="font-medium">
                                                        <SingleSelect.ItemText>
                                                            None
                                                        </SingleSelect.ItemText>
                                                    </p>
                                                    <p className="text-gray-600">
                                                        Don't forward incoming SMS to my email.
                                                    </p>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item
                                                    value="known"
                                                    className="flex-col items-start gap-1"
                                                >
                                                    <p className="font-medium">
                                                        <SingleSelect.ItemText>
                                                            Known contacts only
                                                        </SingleSelect.ItemText>
                                                    </p>
                                                    <p className="text-gray-600">
                                                        Forward incoming SMS sent from people in my
                                                        Contacts only.
                                                    </p>
                                                </SingleSelect.Item>
                                                <SingleSelect.Item
                                                    value="all"
                                                    className="flex-col items-start gap-1"
                                                >
                                                    <p className="font-medium">
                                                        <SingleSelect.ItemText>
                                                            All incoming texts
                                                        </SingleSelect.ItemText>
                                                    </p>
                                                    <p className="text-gray-600">
                                                        Forward all incoming SMS to my email,
                                                        regardless of sender.
                                                    </p>
                                                </SingleSelect.Item>
                                            </SingleSelect.Content>
                                        </Form.SingleSelect>
                                    </Form.Container>
                                    {forwardingCriteria !== 'none' && (
                                        <Form.Container>
                                            <Form.Label name={pathAutoResponse('forwardingEmail')}>
                                                Email
                                            </Form.Label>
                                            <Form.TextInputRoot
                                                control={controlAutoResponse}
                                                name={pathAutoResponse('forwardingEmail')}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faEnvelope}
                                                    className="text-gray-300"
                                                />
                                                <Form.TextInputForm
                                                    type="email"
                                                    placeholder="Email Address"
                                                />
                                            </Form.TextInputRoot>
                                        </Form.Container>
                                    )}
                                    <div className="flex justify-end">
                                        <Form.SubmitButton />
                                    </div>
                                </form>
                            </Form.GivebutterFormProvider>
                        </Collapsible.Content>
                    </Collapsible.Root>
                </div>
            )}

            {(!data?.onboarding.has_sent_sms ||
                !data?.applicationApproved ||
                !data?.onboarding.has_number ||
                !data?.onboarding.has_organization) && (
                <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white pb-6 shadow-sm">
                    <div className="flex flex-col gap-4 p-8 pb-0">
                        <img src={EngageSmsHeader} alt={'Enhance your fundraising with SMS!'} />
                        {!hasDismissedSMSFeatureCallout && (
                            <div className="relative flex flex-col gap-4 rounded-2xl bg-gray-50 p-6">
                                <Button
                                    variant="transparent"
                                    shape={'square'}
                                    className="absolute right-3 top-3 text-gray-500"
                                    onClick={() => {
                                        setHasDismissedSMSFeatureCallout(true);
                                    }}
                                    aria-label="Close"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                                <div className="grid grid-cols-3 gap-6">
                                    <div className="text-center">
                                        <FontAwesomeIcon
                                            icon={faWandMagicSparkles}
                                            size="lg"
                                            className="mb-2"
                                        />
                                        <div className="text-sm font-medium">
                                            Keyword Auto-responder
                                        </div>
                                        <div className="text-xs">
                                            Streamline communication by setting up auto-responses to
                                            common inquiries.
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <FontAwesomeIcon
                                            icon={faDollar}
                                            size="lg"
                                            className="mb-2"
                                        />
                                        <div className="text-sm font-medium">Revenue Tracking</div>
                                        <div className="text-xs">
                                            Easily track the revenue performance of each SMS
                                            marketing campaign.
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        <FontAwesomeIcon
                                            icon={faShield}
                                            size="lg"
                                            className="mb-2"
                                        />
                                        <div className="text-sm font-medium">
                                            Secure & Compliant
                                        </div>
                                        <div className="text-xs">
                                            Ensure that your messages are legal, compliant, and meet
                                            all industry standards.
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-6 gap-6">
                                    <div className="col-span-2 col-start-2 text-center">
                                        <FontAwesomeIcon icon={faSms} size="lg" className="mb-2" />
                                        <div className="text-sm font-medium">
                                            Free Text Messages
                                        </div>
                                        <div className="text-xs">
                                            Stay connected with your supporters for free with SMS
                                            text messages.
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-center">
                                        <FontAwesomeIcon
                                            icon={faInputNumeric}
                                            size="lg"
                                            className="mb-2"
                                        />
                                        <div className="text-sm font-medium">
                                            Unique Phone Number
                                        </div>
                                        <div className="text-xs">
                                            Select your own, custom 10-digit-long code (10DLC) for
                                            supporters to text you with.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="flex items-center gap-1 text-lg font-medium">
                            Get started with SMS checklist
                            <Popover.Root
                                open={openGetStartedPopover}
                                onOpenChange={setOpenGetStartedPopover}
                            >
                                <Popover.Trigger
                                    onMouseEnter={handleMouseEnterGetStartedPopover}
                                    onMouseLeave={handleMouseLeaveGetStartedPopover}
                                >
                                    <FontAwesomeIcon icon={faQuestionCircle} size={'xs'} />
                                </Popover.Trigger>
                                <Popover.Portal>
                                    <Popover.Content
                                        className="max-w-sm"
                                        onMouseEnter={handleMouseEnterGetStartedPopover}
                                        onMouseLeave={handleMouseLeaveGetStartedPopover}
                                    >
                                        <div className="relative p-6">
                                            <div className="text-xl font-medium">
                                                About SMS Registration
                                            </div>
                                            <div>
                                                By registering your custom 10DLC phone number, you
                                                can ensure that your organization's text messages
                                                will be delivered safely, legally, and in compliance
                                                with all major cellular carriers. This helps to
                                                ensure that your messages are reliably delivered to
                                                your intended recipients.
                                                <div className="mt-4 grid grid-cols-3 items-center gap-10">
                                                    <img src={AttLogo} alt="AT&T Logo" />
                                                    <img src={TmobileLogo} alt="T-Mobile Logo" />
                                                    <img src={VerizonLogo} alt="Verizon Logo" />
                                                </div>
                                            </div>
                                            <Popover.Close className="absolute right-3 top-3" />
                                            <Popover.Arrow />
                                        </div>
                                    </Popover.Content>
                                </Popover.Portal>
                            </Popover.Root>
                        </div>
                        <div className="flex flex-col gap-2">
                            <span>
                                You've completed{' '}
                                <span className="font-medium">{stepsCompleted}</span> out of{' '}
                                <span className="font-medium">4 steps</span>!
                            </span>
                            <StepsCompletedBars stepsCompleted={stepsCompleted} />
                        </div>
                    </div>
                    <Accordion.Root type="single" value={currentStep} className="px-8">
                        <Accordion.Item
                            value={SMSConfigurationSteps.VerifyAccount}
                            disabled={stepsCompleted >= 1}
                        >
                            <Accordion.Trigger>
                                <AccordionItemTriggerContent
                                    isCompleted={stepsCompleted >= 1}
                                    isCurrent={currentStep === SMSConfigurationSteps.VerifyAccount}
                                >
                                    Verify your nonprofit
                                </AccordionItemTriggerContent>
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <div className="flex flex-col gap-4 px-11 pb-6 text-sm">
                                    Before registering your phone number with the cellular carriers,
                                    please confirm your identity and verify your nonprofit.
                                    <Link
                                        className="flex items-center gap-1 self-start rounded bg-blue-500 px-3.5 py-1.5 font-medium text-white hover:text-white"
                                        to={`/accounts/${accountId}/settings/about/verify`}
                                    >
                                        Account Verification
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </Link>
                                </div>
                            </Accordion.Content>
                        </Accordion.Item>
                        <Accordion.Item
                            value={SMSConfigurationSteps.SubmitApplication}
                            disabled={stepsCompleted >= 2}
                        >
                            <Accordion.Trigger>
                                <AccordionItemTriggerContent
                                    isCompleted={stepsCompleted >= 2}
                                    isCurrent={
                                        currentStep === SMSConfigurationSteps.SubmitApplication
                                    }
                                >
                                    Submit application
                                </AccordionItemTriggerContent>
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <div className="flex flex-col gap-6 px-11 pb-6 text-sm">
                                    <p>
                                        By submitting your application, Givebutter will register
                                        your brand with all major cellular carriers to ensure safe,
                                        legal, and reliable delivery of your text messages.
                                    </p>

                                    {messageNumber?.provider === 'twilio' && (
                                        <div className="bg-red-25 flex gap-3 rounded-lg border border-red-300 p-3 text-sm text-red-700">
                                            <FontAwesomeIcon
                                                icon={faExclamationCircle}
                                                size={'lg'}
                                                className="text-red-500"
                                            />
                                            <div className="flex flex-col gap-3">
                                                <div>
                                                    <p className="font-medium">
                                                        Remove number before submitting application
                                                    </p>
                                                    <p>
                                                        To complete your registration for compliance
                                                        with TCR by the July 31, 2023 deadline,
                                                        you'll need to remove the Engage number (
                                                        {getFormattedPhoneNumber(
                                                            data?.channels.sms.number?.value ?? '',
                                                        )}
                                                        ) attached to your account. Please note that
                                                        you'll be unable to send messages until your
                                                        application is approved, which usually takes
                                                        2-7 days.
                                                    </p>
                                                </div>
                                                <div className="flex justify-end">
                                                    <Button
                                                        variant="solid"
                                                        size="sm"
                                                        color="error"
                                                        onClick={() => deleteEngageMessageNumber()}
                                                    >
                                                        Remove number and continue
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {messageNumber?.provider !== 'twilio' && (
                                        <>
                                            <div className="grid grid-cols-3 gap-6 rounded-xl bg-gray-50 p-4">
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-xs font-medium">
                                                        {sponsoringLegalEntity
                                                            ? 'Sponsoring organization'
                                                            : 'Organization name'}
                                                    </p>
                                                    <p className="text-sm">
                                                        {legalEntity?.name ||
                                                            sponsoringLegalEntity?.name}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-xs font-medium">
                                                        {sponsoringLegalEntity
                                                            ? 'Sponsoring EIN'
                                                            : 'EIN'}
                                                    </p>
                                                    <p className="text-sm">
                                                        {legalEntity?.ein ||
                                                            sponsoringLegalEntity?.ein}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-xs font-medium">
                                                        Email address
                                                    </p>

                                                    {!!email && <p className="text-sm">{email}</p>}

                                                    {!email && (
                                                        <p className="text-sm text-red-600">
                                                            <Link
                                                                to={`/accounts/${accountId}/settings/about`}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faExclamationCircle}
                                                                />{' '}
                                                                Required
                                                            </Link>
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-xs font-medium">Address</p>
                                                    <p className="text-sm">{postalAddress}</p>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-xs font-medium">Domain</p>
                                                    <p className="text-sm">
                                                        {website?.replace(
                                                            /^(https?:\/\/)?(www\.)?/i,
                                                            '',
                                                        ) || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-xs font-medium">
                                                        Contact number
                                                    </p>

                                                    {!!phone && (
                                                        <p className="text-sm">
                                                            {getFormattedPhoneNumber(phone ?? '')}
                                                        </p>
                                                    )}

                                                    {!phone && (
                                                        <p className="text-sm text-red-600">
                                                            <Link
                                                                to={`/accounts/${accountId}/settings/about`}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faExclamationCircle}
                                                                />{' '}
                                                                Required
                                                            </Link>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex w-full justify-end">
                                                <Button
                                                    variant="solid"
                                                    className={twMerge(
                                                        'px-3.5 py-1.5',
                                                        data?.applicationPending &&
                                                            '!bg-orange-100 text-orange-500',
                                                    )}
                                                    disabled={
                                                        !email ||
                                                        !phone ||
                                                        data?.applicationPending ||
                                                        isPendingAPIRequest
                                                    }
                                                    onClick={() => {
                                                        setIsPendingAPIRequest(true);
                                                        provisionEngageRegistration();
                                                    }}
                                                >
                                                    {isPendingAPIRequest
                                                        ? 'Submitting...'
                                                        : submitBtnText}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Accordion.Content>
                        </Accordion.Item>
                        <Accordion.Item
                            value={SMSConfigurationSteps.SelectPhoneNumber}
                            disabled={stepsCompleted >= 3}
                        >
                            <Accordion.Trigger>
                                <AccordionItemTriggerContent
                                    isCompleted={stepsCompleted >= 3}
                                    isCurrent={
                                        currentStep === SMSConfigurationSteps.SelectPhoneNumber
                                    }
                                >
                                    Select your phone number (10DLC)
                                </AccordionItemTriggerContent>
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <div className="flex flex-col gap-4 px-11 pb-6 text-sm">
                                    <p className="text-sm">
                                        Search for an area code and select a number for your account
                                        to send messages with. Once you select a number, you won't
                                        be able to change it.
                                    </p>
                                    <Form.GivebutterFormProvider {...methods}>
                                        <form
                                            className="flex items-center gap-2"
                                            onSubmit={handleSubmit(() => {
                                                getAvailableNumbers();
                                            })}
                                        >
                                            <FontAwesomeIcon
                                                icon={faMagnifyingGlass}
                                                className="text-gray-300"
                                            />
                                            <Form.TextInputRoot control={control} name={'search'}>
                                                <Form.TextInputForm placeholder="Search state or area code" />
                                            </Form.TextInputRoot>
                                            <Button
                                                variant="solid"
                                                type="submit"
                                                disabled={isPendingAPIRequest}
                                            >
                                                Search
                                            </Button>
                                        </form>
                                    </Form.GivebutterFormProvider>

                                    {availableNumbers.length === 0 && (
                                        <div className="flex flex-col items-center justify-center p-8 text-center">
                                            <div className="rounded-full bg-gray-200 p-3">
                                                <FontAwesomeIcon
                                                    icon={faPhoneSlash}
                                                    size={'lg'}
                                                    className="text-gray-600"
                                                />
                                            </div>
                                            <span className="font-medium text-gray-700">
                                                No Numbers Found
                                            </span>
                                            Enter a different state or area code
                                        </div>
                                    )}

                                    {isPendingAPIRequest && availableNumbers.length === 0 && (
                                        <div className="flex items-center justify-center gap-1 p-8">
                                            <FontAwesomeIcon icon={faSpinner} spin={true} />
                                            Searching for phone numbers...
                                        </div>
                                    )}

                                    {!!availableNumbers.length && (
                                        <>
                                            <RadioGroup.Root
                                                value={selectedNumber}
                                                onValueChange={value => {
                                                    setSelectedNumber(value);
                                                }}
                                            >
                                                <div className="grid grid-cols-2 gap-3">
                                                    {availableNumbers.map(numberOption => {
                                                        return (
                                                            <Label
                                                                htmlFor={`option${numberOption}`}
                                                                key={numberOption}
                                                                className={`flex cursor-pointer items-center gap-2 rounded-lg p-3.5 text-sm font-medium ${
                                                                    selectedNumber === numberOption
                                                                        ? 'bg-blue-25 border-2 border-blue-400'
                                                                        : 'bg-gray-25 border border-gray-300'
                                                                }`}
                                                            >
                                                                <RadioGroup.Item
                                                                    value={numberOption}
                                                                    id={`option${numberOption}`}
                                                                >
                                                                    <RadioGroup.Indicator />
                                                                </RadioGroup.Item>

                                                                {isPendingAPIRequest ? (
                                                                    <FontAwesomeIcon
                                                                        icon={faSpinner}
                                                                        spin={true}
                                                                    />
                                                                ) : (
                                                                    numberOption
                                                                )}
                                                            </Label>
                                                        );
                                                    })}
                                                </div>
                                            </RadioGroup.Root>
                                            <div className="flex flex-col gap-4 text-right">
                                                <div className="flex justify-end">
                                                    <Button
                                                        variant="outline"
                                                        color="neutral"
                                                        shape={'square'}
                                                        onClick={() => {
                                                            setSelectedNumber('');
                                                            getAvailableNumbers();
                                                        }}
                                                        disabled={isPendingAPIRequest}
                                                        aria-label="Refresh"
                                                    >
                                                        <FontAwesomeIcon icon={faRepeat} />
                                                    </Button>
                                                </div>
                                                <div className="flex justify-end">
                                                    <Button
                                                        variant="solid"
                                                        size="sm"
                                                        disabled={
                                                            selectedNumber.trim() === '' ||
                                                            isPendingAPIRequest
                                                        }
                                                        onClick={() => {
                                                            postPhoneNumber();
                                                        }}
                                                    >
                                                        Select Number
                                                    </Button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Accordion.Content>
                        </Accordion.Item>
                        <Accordion.Item
                            value={SMSConfigurationSteps.SendFirstMessage}
                            disabled={stepsCompleted >= 4}
                        >
                            <Accordion.Trigger>
                                <AccordionItemTriggerContent
                                    isCompleted={stepsCompleted >= 4}
                                    isCurrent={
                                        currentStep === SMSConfigurationSteps.SendFirstMessage
                                    }
                                >
                                    Send your first text message!
                                </AccordionItemTriggerContent>
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <div className="flex flex-col gap-4 px-11 pb-6 text-sm">
                                    Your account has been approved and is ready to start using SMS
                                    to fundraise.
                                    <div className="flex justify-end">
                                        <Button
                                            variant="solid"
                                            size="sm"
                                            onClick={() => {
                                                onClickCreateMessage();
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            New Message
                                        </Button>
                                    </div>
                                </div>
                            </Accordion.Content>
                        </Accordion.Item>
                    </Accordion.Root>
                </div>
            )}

            <AlertDialog.Root
                type={'success'}
                open={showApplicationSentModal}
                onOpenChange={isOpen => {
                    setShowApplicationSentModal(isOpen);
                }}
            >
                <AlertDialog.Portal>
                    <AlertDialog.Overlay />
                    <AlertDialog.Content>
                        <AlertDialog.Header>
                            <AlertDialog.Icon />
                            <AlertDialog.Close />
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <AlertDialog.Title>Application Sent!</AlertDialog.Title>
                            <AlertDialog.Description>
                                The approval process may take up to 14 days depending upon if there
                                is a discrepancy in the information provided.
                            </AlertDialog.Description>
                        </AlertDialog.Body>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </div>
    );
};

interface UseCurrentStepArgs {
    engageConfiguration: EngageConfiguration & {
        applicationPending: boolean;
        applicationApproved: boolean;
    };
}

const useCurrentStep = ({ engageConfiguration }: UseCurrentStepArgs) => {
    let stepsCompleted = 0;
    let currentStep = SMSConfigurationSteps.VerifyAccount;

    if (
        engageConfiguration?.onboarding.has_organization ||
        engageConfiguration?.onboarding.has_sponsoring_organization
    ) {
        stepsCompleted++;
        currentStep = SMSConfigurationSteps.SubmitApplication;
    }

    if (engageConfiguration?.applicationApproved) {
        stepsCompleted++;
        currentStep = SMSConfigurationSteps.SelectPhoneNumber;
    }

    if (engageConfiguration?.applicationApproved && engageConfiguration?.onboarding.has_number) {
        stepsCompleted++;
        currentStep = SMSConfigurationSteps.SendFirstMessage;
    }

    return { currentStep, stepsCompleted };
};

interface StepsCompletedBarsProps {
    stepsCompleted: number;
}

const StepsCompletedBars = ({ stepsCompleted }: StepsCompletedBarsProps) => {
    return (
        <div className="flex gap-0.5">
            <div
                className={twMerge(
                    'h-2 flex-grow rounded-bl-2xl rounded-tl-2xl bg-blue-200',
                    stepsCompleted !== 0 && 'bg-blue-500',
                )}
            />
            <div
                className={twMerge(
                    'h-2 flex-grow bg-gray-200',
                    stepsCompleted > 1 && 'bg-blue-500',
                    stepsCompleted === 1 && 'bg-blue-200',
                )}
            />
            <div
                className={twMerge(
                    'h-2 flex-grow bg-gray-200',
                    stepsCompleted > 2 && 'bg-blue-500',
                    stepsCompleted === 2 && 'bg-blue-200',
                )}
            />
            <div
                className={twMerge(
                    'h-2 flex-grow rounded-br-2xl rounded-tr-2xl bg-gray-200',
                    stepsCompleted > 3 && 'bg-blue-500',
                    stepsCompleted === 3 && 'bg-blue-200',
                )}
            />
        </div>
    );
};

interface AccordionItemTriggerContentProps extends PropsWithChildren {
    isCompleted: boolean;
    isCurrent: boolean;
}

const AccordionItemTriggerContent = ({
    children,
    isCompleted,
    isCurrent,
}: AccordionItemTriggerContentProps) => {
    return (
        <>
            <span className="flex items-center gap-3">
                <FontAwesomeIcon
                    icon={isCompleted ? faCircleCheck : faCircle}
                    className={twMerge(isCurrent && 'text-blue-500')}
                />
                <span className={twMerge('text-md font-medium', isCurrent && 'text-blue-500')}>
                    {children}
                </span>
            </span>
            <Accordion.ItemIndicator />
        </>
    );
};
export default NumberSettings;
